import React from "react";

import {
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  CloudProvider,
  StackTemplate,
  StackType,
  TemplateComplexity,
} from "../../types";

interface Props {
  stackType: StackType;
  cloudProvider: CloudProvider;
  selectedTemplate: StackTemplate;
  name: string;
  region: string;
  onNameChange: (name: string) => void;
  onRegionChange: (region: string) => void;
}

const REGIONS: Record<CloudProvider, { value: string; label: string }[]> = {
  [CloudProvider.AWS]: [
    { value: "us-east-1", label: "US East (N. Virginia)" },
    { value: "us-west-2", label: "US West (Oregon)" },
    { value: "eu-west-1", label: "EU (Ireland)" },
    { value: "eu-central-1", label: "EU (Frankfurt)" },
    { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  ],
  [CloudProvider.GCP]: [
    { value: "us-central1", label: "US Central (Iowa)" },
    { value: "us-east1", label: "US East (South Carolina)" },
    { value: "europe-west1", label: "Europe West (Belgium)" },
    { value: "europe-west4", label: "Europe West (Netherlands)" },
    { value: "asia-east1", label: "Asia East (Taiwan)" },
  ],
  [CloudProvider.AZURE]: [
    { value: "eastus", label: "East US" },
    { value: "westus2", label: "West US 2" },
    { value: "westeurope", label: "West Europe" },
    { value: "northeurope", label: "North Europe" },
    { value: "southeastasia", label: "Southeast Asia" },
  ],
  [CloudProvider.SELF_HOSTED]: [{ value: "on-premises", label: "On-premises" }],
  [CloudProvider.RUSHA_HOSTED]: [
    { value: "rusha-cloud", label: "Rusha Cloud" },
  ],
};

const COMPLEXITY_COLOR: Record<
  TemplateComplexity,
  "success" | "warning" | "error"
> = {
  low: "success",
  medium: "warning",
  high: "error",
};

const TOOL_LABELS: Record<string, string> = {
  airbyte: "Airbyte",
  debezium_kafka: "Debezium + Kafka",
  kafka: "Kafka",
  meltano: "Meltano",
  dbt: "dbt",
  spark: "Spark",
  flink: "Flink",
  dbt_duckdb: "dbt + DuckDB",
  iceberg: "Iceberg",
  delta_lake: "Delta Lake",
  hudi: "Hudi",
  hive_metastore: "Hive Metastore",
  polaris: "Polaris",
  nessie: "Nessie",
  trino: "Trino",
  spark_sql: "Spark SQL",
  duckdb: "DuckDB",
  postgres: "PostgreSQL",
  dagster: "Dagster",
  airflow: "Airflow",
  prefect: "Prefect",
  superset: "Superset",
  metabase: "Metabase",
  grafana: "Grafana",
  beaver: "Beaver",
  clickhouse: "ClickHouse",
  starrocks: "StarRocks",
  druid: "Druid",
  pinot: "Pinot",
  redash: "Redash",
  iceberg_catalog: "Iceberg Catalog",
  olap_load: "OLAP Load",
  kafka_stream: "Kafka Stream",
  relational_db: "Relational DB",
  react: "React",
  nextjs: "Next.js",
  vue: "Vue",
  angular: "Angular",
  svelte: "Svelte",
  fastapi: "FastAPI",
  django: "Django",
  nestjs: "NestJS",
  express: "Express",
  spring_boot: "Spring Boot",
  go_fiber: "Go Fiber",
  postgresql: "PostgreSQL",
  mysql: "MySQL",
  mongodb: "MongoDB",
  sqlite: "SQLite",
  bigquery: "BigQuery",
  redis: "Redis",
  memcached: "Memcached",
  rabbitmq: "RabbitMQ",
};

function stackConfigTools(config: StackTemplate["stackConfig"]): string[] {
  const tools: string[] = [];
  for (const val of Object.values(config)) {
    if (val && typeof val === "string" && val !== "none") {
      tools.push(TOOL_LABELS[val] ?? val);
    }
  }
  return tools;
}

function toAlias(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function StepNameAndConfirm({
  stackType,
  cloudProvider,
  selectedTemplate,
  name,
  region,
  onNameChange,
  onRegionChange,
}: Props) {
  const alias = toAlias(name);
  const regionOptions = REGIONS[cloudProvider] ?? [];
  const tools = stackConfigTools(selectedTemplate.stackConfig);

  function handleRegionChange(e: SelectChangeEvent) {
    onRegionChange(e.target.value);
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Name and confirm
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Give your stack a name, choose a region, then review the configuration.
      </Typography>

      <Stack spacing={3}>
        <TextField
          label="Stack name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. Data Lakehouse"
          fullWidth
          required
          helperText={
            alias ? `Alias: ${alias}` : "Enter a name to generate an alias"
          }
        />

        <FormControl fullWidth required>
          <InputLabel>Region</InputLabel>
          <Select value={region} label="Region" onChange={handleRegionChange}>
            {regionOptions.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Configuration summary
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: 120 }}
              >
                Type
              </Typography>
              <Chip
                label={
                  stackType === StackType.DATA_ENGINEERING
                    ? "Data Engineering"
                    : stackType === StackType.ANALYTICAL
                      ? "Analytical"
                      : "Web Application"
                }
                size="small"
                color="primary"
                variant="outlined"
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: 120 }}
              >
                Cloud
              </Typography>
              <Chip label={cloudProvider} size="small" variant="outlined" />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: 120, pt: 0.25 }}
              >
                Template
              </Typography>
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {selectedTemplate.name}
                  </Typography>
                  <Chip
                    label={
                      selectedTemplate.complexity.charAt(0).toUpperCase() +
                      selectedTemplate.complexity.slice(1)
                    }
                    color={COMPLEXITY_COLOR[selectedTemplate.complexity]}
                    size="small"
                    sx={{ height: 18, fontSize: "0.62rem" }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontSize: "0.78rem" }}
                >
                  {selectedTemplate.description}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {tools.map((tool) => (
                    <Chip
                      key={tool}
                      label={tool}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.65rem" }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
